import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { productApi } from '@/api/commercial/products';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import type { IProductCategory } from '@/types/commercial/product';

interface DeleteCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: IProductCategory | null;
  onSuccess?: () => void;
}

export function DeleteCategoryDialog({
  open,
  onOpenChange,
  category,
  onSuccess,
}: DeleteCategoryDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleDelete = async () => {
    if (!category) return;

    setIsLoading(true);
    try {
      const result = await productApi.deleteCategory(category.id);

      if (result.success) {
        toast({
          variant: 'success',
          title: 'Sucesso',
          description: 'Categoria excluída com sucesso!',
        });
        onOpenChange(false);
        onSuccess?.();
      } else {
        toast({
          variant: 'destructive',
          title: 'Erro',
          description:
            result.error?.global ||
            'Erro ao excluir categoria. Verifique se não há produtos vinculados.',
        });
      }
    } catch {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao excluir categoria. Tente novamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-500" />
            Excluir Categoria
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Tem certeza que deseja excluir a categoria{' '}
            <strong>
              {category?.code} - {category?.name}
            </strong>
            ?
            <br />
            <br />
            <span className="text-red-600">
              Esta ação não pode ser desfeita. Categorias com produtos vinculados não podem ser
              excluídas.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isLoading}>
            {isLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
